/**
 * Sidebar source-of-truth. The DocsLayout sidebar walks this tree
 * and the DocPage uses the slugs to load MDX via content.ts.
 *
 * Each leaf has a slug that matches src/content/<lang>/<slug>.mdx.
 * Labels live in i18n/messages/* keyed by `manifest.<slug>` — fall
 * back to the literal string here if the key is missing.
 */

export interface ManifestLeaf {
  slug: string;
  /** Default label shown when the i18n key is absent. */
  label: string;
}

export interface ManifestSection {
  /** i18n key root; the section label is `manifest.section.<key>`. */
  key: string;
  /** Default label shown when the i18n key is absent. */
  label: string;
  items: ManifestLeaf[];
}

export const MANIFEST: ManifestSection[] = [
  {
    key: 'overview',
    label: 'Overview',
    items: [
      { slug: 'overview',        label: 'What is Metha'   },
      { slug: 'why-dvbc',        label: 'Why DVBC'        },
      { slug: 'status',          label: 'Status & roadmap' },
    ],
  },
  {
    key: 'how-it-works',
    label: 'How it works',
    items: [
      { slug: 'how-it-works/value-loop',  label: 'The value loop' },
      { slug: 'how-it-works/auctions',    label: 'Periodic auctions' },
      { slug: 'how-it-works/requests',    label: 'Funding requests' },
      { slug: 'how-it-works/consensus',   label: 'Auditor consensus' },
      { slug: 'how-it-works/governance',  label: 'Governance' },
    ],
  },
  {
    key: 'architecture',
    label: 'Architecture',
    items: [
      { slug: 'architecture',             label: 'System diagram' },
      { slug: 'architecture/contracts',   label: 'Six contracts' },
      { slug: 'architecture/data-flow',   label: 'Data flow' },
      { slug: 'architecture/evolution',   label: 'Slot registry & evolution' },
    ],
  },
  {
    key: 'concepts',
    label: 'Core concepts',
    items: [
      { slug: 'concepts/dvbc',            label: 'DVBC' },
      { slug: 'concepts/three-conditions',label: 'Three conditions' },
      { slug: 'concepts/mechanisms-abc',   label: 'Mechanisms A / B / C' },
      { slug: 'concepts/typed-deliverables', label: 'Typed deliverables' },
      { slug: 'concepts/barda-option',    label: 'BARDA option-period' },
      { slug: 'concepts/dsmb-veto',       label: 'DSMB veto' },
      { slug: 'concepts/slashing',        label: 'Slashing tiers' },
    ],
  },
  {
    key: 'guides',
    label: 'User guides',
    items: [
      { slug: 'guides/lab',               label: 'For labs' },
      { slug: 'guides/auditor',           label: 'For auditor firms' },
      { slug: 'guides/holder',            label: 'For token holders' },
      { slug: 'guides/dsmb',              label: 'For DSMB members' },
      { slug: 'guides/program-officer',   label: 'For Program Officers' },
      { slug: 'guides/developer',         label: 'For developers' },
      { slug: 'guides/developer/mdx-components', label: 'MDX components reference' },
    ],
  },
  {
    key: 'security',
    label: 'Security',
    items: [
      { slug: 'security/slither',         label: 'Slither report summary' },
      { slug: 'security/audits',          label: 'Audit sequence' },
      { slug: 'security/reporting',       label: 'Report a vulnerability' },
    ],
  },
  {
    key: 'risks',
    label: 'Risks',
    items: [
      { slug: 'risks',                    label: 'Risk register' },
    ],
  },
  {
    key: 'future',
    label: 'Future developments',
    items: [
      { slug: 'future/roadmap',           label: 'Roadmap' },
      { slug: 'future/v4-iteration',      label: 'v4 iteration plan' },
      { slug: 'future/research',          label: 'Open research questions' },
    ],
  },
  {
    key: 'literature',
    label: 'Literature',
    items: [
      { slug: 'literature',               label: 'Papers & references' },
    ],
  },
  {
    key: 'glossary',
    label: 'Glossary',
    items: [
      { slug: 'glossary',                 label: 'Glossary' },
    ],
  },
  {
    key: 'faq',
    label: 'FAQ',
    items: [
      { slug: 'faq',                      label: 'FAQ' },
    ],
  },
  {
    key: 'changelog',
    label: 'Changelog',
    items: [
      { slug: 'changelog',                label: 'Changelog' },
    ],
  },
];
